import type { WatchdogProposal, PresentationState } from '../types';

interface Props {
  item: WatchdogProposal;
  selected: boolean;
  onClick: () => void;
}

const RISK_LABEL: Record<string, string> = {
  low: 'Nízké',
  medium: 'Střední',
  high: 'Vysoké',
  critical: 'Kritické',
};

type StateBadge = { text: string; className: string };

function stateBadge(state: PresentationState | string): StateBadge {
  switch (state) {
    case 'completed':
      return { text: 'Dokončeno', className: 'badge badge--ok' };
    case 'placeholder':
      return { text: 'Placeholder', className: 'badge badge--placeholder' };
    case 'failed_retryable':
      return { text: 'Chyba (retry)', className: 'badge badge--alert' };
    case 'no_pdf':
      return { text: 'PDF nedostupné', className: 'badge badge--incomplete' };
    case 'error':
      return { text: 'Chyba', className: 'badge badge--alert' };
    case 'incomplete':
    default:
      return { text: 'Rozpracováno', className: 'badge badge--incomplete' };
  }
}

function alertBadge(item: WatchdogProposal): StateBadge | null {
  if (!item.final_decision) return null;
  if (item.presentation_state !== 'completed') return null;
  const alert = item.final_decision.alert;
  if (alert === 'yes') return { text: 'ALERT', className: 'badge badge--alert' };
  if (alert === 'no') return { text: 'Bez alertu', className: 'badge badge--ok' };
  return null;
}

// V-20 additive erratum. A human review either degraded the published analysis
// (kept within the published range, no alert) or returned it to the curator. The
// badge is purely additive -- it never replaces the state/alert badges.
function correctionBadge(item: WatchdogProposal): StateBadge | null {
  const correction = item.correction;
  if (!correction) return null;
  if (correction.state === 'returned_to_curator') {
    return { text: 'Vráceno kurátorovi', className: 'badge badge--correction' };
  }
  if (correction.state === 'degraded') {
    return { text: 'Korekce (degradováno)', className: 'badge badge--correction' };
  }
  return null;
}

function riskLevel(item: WatchdogProposal): string {
  return item.final_decision?.final_risk_level
    ?? item.third_pass?.final_risk_level
    ?? item.second_pass?.final_risk_level
    ?? item.first_pass?.risk_level
    ?? '';
}

export function WatchdogCard({ item, selected, onClick }: Props) {
  const badge = stateBadge(item.presentation_state as PresentationState);
  const risk = riskLevel(item);
  const alert = alertBadge(item);
  const correction = correctionBadge(item);
  const summary = item.final_decision?.plain_explanation
    ?? item.third_pass?.plain_explanation
    ?? item.second_pass?.plain_explanation
    ?? item.first_pass?.plain_explanation
    ?? '';

  return (
    <article
      className={`analysis-card analysis-card--risk-${risk} ${selected ? 'analysis-card--selected' : ''} watchdog-card`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
      aria-selected={selected}
      aria-label={`${item.title} — ${badge.text}`}
    >
      <div className="analysis-card__header">
        {risk && (
          <span className={`risk-badge risk-badge--${risk}`}>
            {RISK_LABEL[risk] ?? risk}
          </span>
        )}
        <span className={badge.className}>{badge.text}</span>
        {alert && <span className={alert.className}>{alert.text}</span>}
        {correction && (
          <span
            className={correction.className}
            title={item.correction?.corrected_at ? `Korekce z ${item.correction.corrected_at}` : 'Korekce po lidské revizi'}
          >
            {correction.text}
          </span>
        )}
        <span className="analysis-card__area">{item.processing_stage}</span>
      </div>

      <h3 className="analysis-card__title">{item.title}</h3>

      {summary && (
        <p className="analysis-card__summary">{summary}</p>
      )}
    </article>
  );
}
